import {
  Box,
  Flex,
  useDisclosure,
  Button,
  Select,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
} from "@chakra-ui/react";
import SettingsDescription from "pages/Settings/elements/SettingsDescription";
import SettingsHeader from "pages/Settings/elements/SettingsHeader";
import { useCallback, useEffect, useState } from "react";
import { apiRequest, apiGetRequest } from "utils/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Loader } from "components/elements/loader/loader";
import { ChecklistItem } from "./checklist-item";
import { ChecklistItemModal } from "./checklist-item-modal";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { permissionConstants, hasPermission, isPermissionEnabled } from "utils/permissionHelper";
import { Tables } from "types/supabase";
import { ChecklistItemViewModel } from "./types";
import { AddIcon } from "@chakra-ui/icons";
import { AssignmentsModal } from "./assignments-modal";

export function ChecklistSettings() {
  const { user } = useTenant();
  const [checklistItemForModal, setChecklistItemForModal] = useState<ChecklistItemViewModel | null>(
    null
  );
  const [checklistItems, setChecklistItems] = useState<ChecklistItemViewModel[]>([]);
  const [filteredChecklistItems, setFilteredChecklistItems] = useState<ChecklistItemViewModel[]>(
    []
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [loanTypes, setLoanTypes] = useState<Tables<"loan_types">[]>([]);
  const [selectedLoanType, setSelectedLoanType] = useState<string>("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checklistItemForAssignments, setChecklistItemForAssignments] =
    useState<ChecklistItemViewModel | null>(null);
  const {
    isOpen: isAssignmentsModalOpen,
    onOpen: onAssignmentsModalOpen,
    onClose: onAssignmentsModalClose,
  } = useDisclosure();

  const canEditChecklist = hasPermission(user, permissionConstants.editChecklist);
  const canAddChecklist = hasPermission(user, permissionConstants.addChecklist);
  const canViewChecklist = canAddChecklist || canEditChecklist || !isPermissionEnabled(user);

  const loadLoanTypes = useCallback(async () => {
    try {
      const { loan_types } = await apiGetRequest<{
        loan_types: Tables<"loan_types">[];
      }>("/supabase/loan_types/select");

      // Filter loan types for the current company
      const companyLoanTypes = loan_types.filter(type => type.lender_company === user.company.id);

      setLoanTypes(companyLoanTypes);
      if (companyLoanTypes.length > 0) {
        setSelectedLoanType(companyLoanTypes[0].id);
      }
    } catch (error) {
      console.error("Failed to load loan types:", error);
      setError("Failed to load loan types");
    }
  }, [user.company.id]);

  const filterChecklistItems = useCallback((items: ChecklistItemViewModel[], query: string) => {
    if (!query.trim()) return items;

    const searchLower = query.toLowerCase();
    return items.filter(
      item =>
        item.title.toLowerCase().includes(searchLower) ||
        item.category.toLowerCase().includes(searchLower) ||
        (item.type && item.type.toLowerCase().includes(searchLower))
    );
  }, []);

  const loadChecklistItems = useCallback(async () => {
    if (!canViewChecklist) return;

    setIsLoading(true);
    setError(null);

    try {
      const params: Record<string, any> = {
        deleted: "false",
      };

      if (selectedLoanType) {
        params.loan_type = selectedLoanType;
      }

      const { checklist_items = [] } = await apiGetRequest<{
        checklist_items: Tables<"checklist_items">[];
      }>("/supabase/checklist_items/select", params);

      // Convert checklist items to view model without assignments for now
      const itemsWithAssignments = checklist_items.map(
        item =>
          ({
            id: item.id,
            title: item.title || "",
            category: item.category || "",
            description: item.description || "",
            order: item.order || 0,
            type: item.type || "checklist",
            notes: item.description,
            completed: false,
            loan_type: item.loan_type,
            created_at: item.created_at,
            assignments: item.assignments || [],
          } as ChecklistItemViewModel)
      );

      // Filter items based on user's role
      const filteredItems = itemsWithAssignments.filter(item => {
        if (
          user.type === "borrower" &&
          (!item.assignments || !item.assignments.includes("borrower"))
        ) {
          return false;
        }
        return true;
      });

      const sortedItems = sortChecklistItems(filteredItems);
      setChecklistItems(sortedItems);
      setFilteredChecklistItems(filterChecklistItems(sortedItems, searchQuery));
    } catch (error) {
      console.error("Failed to load checklist items:", error);
      setError("Failed to load checklist items");
    } finally {
      setIsLoading(false);
    }
  }, [canViewChecklist, selectedLoanType, filterChecklistItems, user.type]);

  useEffect(() => {
    loadLoanTypes();
  }, [loadLoanTypes]);

  useEffect(() => {
    if (selectedLoanType) {
      loadChecklistItems();
    }
  }, [loadChecklistItems, selectedLoanType]);

  useEffect(() => {
    setFilteredChecklistItems(filterChecklistItems(checklistItems, searchQuery));
  }, [searchQuery, checklistItems, filterChecklistItems]);

  // Checklist Item Modal
  function handleEdit(checklistItem: ChecklistItemViewModel) {
    if (!canEditChecklist) return;
    setChecklistItemForModal(checklistItem);
    onOpen();
  }

  function handleNewChecklistItem() {
    if (!canAddChecklist) return;
    const newChecklistItem = createNewChecklistItem();
    setChecklistItemForModal(newChecklistItem);
    onOpen();
  }

  function handleCloseChecklistItemModal() {
    setChecklistItemForModal(null);
    onClose();
  }

  async function handleOnChecklistItemDeleted(checklistItem: ChecklistItemViewModel) {
    if (!canEditChecklist) return;

    try {
      await apiRequest("/supabase/checklist_items/delete", {
        id: checklistItem.id,
      });
      setChecklistItems(state => state.filter(x => x.id !== checklistItem.id));
    } catch (error) {
      console.error("Failed to delete checklist item:", error);
    }
  }

  async function handleOnChecklistItemUpdate(checklistItem: ChecklistItemViewModel) {
    if (!canEditChecklist) return;

    try {
      await apiRequest("/supabase/checklist_items/update", {
        id: checklistItem.id,
        title: checklistItem.title,
        category: checklistItem.category,
        description: checklistItem.description,
        order: checklistItem.order,
        type: "checklist",
        assignments: checklistItem.assignments,
      });

      setChecklistItems(items =>
        items.map(item => (item.id === checklistItem.id ? checklistItem : item))
      );
    } catch (error) {
      console.error("Failed to update checklist item:", error);
    }
  }

  async function handleOnChecklistItemAdded(checklistItem: ChecklistItemViewModel) {
    if (!canAddChecklist) return;

    try {
      const { checklist_item } = await apiRequest("/supabase/checklist_items/insert", {
        title: checklistItem.title,
        category: checklistItem.category,
        description: checklistItem.description,
        order: checklistItem.order,
        type: "checklist",
        loan_type: selectedLoanType,
        assignments: checklistItem.assignments,
      });

      const newItem: ChecklistItemViewModel = {
        id: checklist_item.id,
        title: checklist_item.title || "",
        category: checklist_item.category || "",
        description: checklist_item.description || "",
        order: checklist_item.order || 0,
        type: checklist_item.type || "checklist",
        notes: checklist_item.description,
        completed: false,
        loan_type: checklist_item.loan_type,
        created_at: checklist_item.created_at,
        assignments: checklist_item.assignments || [],
      };

      setChecklistItems(state => [...state, newItem]);
    } catch (error) {
      console.error("Failed to add checklist item:", error);
    }
  }

  function handleManageAssignments(checklistItem: ChecklistItemViewModel) {
    if (!canEditChecklist) return;
    setChecklistItemForAssignments(checklistItem);
    onAssignmentsModalOpen();
  }

  function handleCloseAssignmentsModal() {
    setChecklistItemForAssignments(null);
    onAssignmentsModalClose();
  }

  if (!canViewChecklist) {
    return (
      <Flex direction="column" gap={4}>
        <Box>
          <SettingsHeader title="Checklist Items" />
          <SettingsDescription text="You do not have permission to view checklist items" />
        </Box>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap={4}>
      <Box>
        <SettingsHeader title="Checklist Items" />
        <SettingsDescription text="Manage your checklist items and categories" />
      </Box>

      <Box>
        <HStack spacing={4} mb={4} align="center" maxW="600px">
          <Select
            value={selectedLoanType}
            onChange={e => setSelectedLoanType(e.target.value)}
            placeholder="Select loan type"
            flex={1}
          >
            {loanTypes.map(loanType => (
              <option key={loanType.id} value={loanType.id}>
                {loanType.title}
              </option>
            ))}
          </Select>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={handleNewChecklistItem}
            isDisabled={!canAddChecklist || !selectedLoanType}
          >
            Add Checklist
          </Button>
        </HStack>

        <InputGroup mb={4} maxW="600px">
          <InputLeftElement pointerEvents="none">
            <FontAwesomeIcon icon={["far", "search"]} color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search by category or name..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </InputGroup>
      </Box>

      {error && (
        <Text color="red.500" mb={4}>
          {error}
        </Text>
      )}

      {isLoading ? (
        <Loader>Loading Checklist Items...</Loader>
      ) : (
        <>
          <Box maxW="600px">
            {filteredChecklistItems.length === 0 ? (
              <Text color="gray.500">No checklist items found</Text>
            ) : (
              filteredChecklistItems.map(checklistItem => (
                <ChecklistItem
                  key={checklistItem.id}
                  checklistItem={checklistItem}
                  onEdit={handleEdit}
                  onUpdate={handleOnChecklistItemUpdate}
                  onDelete={handleOnChecklistItemDeleted}
                  onManageAssignments={handleManageAssignments}
                  canEdit={canEditChecklist}
                />
              ))
            )}
          </Box>
        </>
      )}

      {checklistItemForModal && (
        <ChecklistItemModal
          checklistItem={checklistItemForModal}
          isOpen={isOpen}
          onClose={handleCloseChecklistItemModal}
          onAdd={handleOnChecklistItemAdded}
          onUpdate={handleOnChecklistItemUpdate}
          loan_type={selectedLoanType}
        />
      )}

      {checklistItemForAssignments && (
        <AssignmentsModal
          checklistItem={checklistItemForAssignments}
          isOpen={isAssignmentsModalOpen}
          onClose={handleCloseAssignmentsModal}
          onUpdate={updatedItem => {
            setChecklistItems(items =>
              items.map(item => (item.id === updatedItem.id ? updatedItem : item))
            );
          }}
        />
      )}
    </Flex>
  );
}

function sortChecklistItems(checklistItems: ChecklistItemViewModel[]) {
  return checklistItems
    .sort((a, b) => a.order - b.order)
    .sort((a, b) => a.category.localeCompare(b.category));
}

function createNewChecklistItem(): ChecklistItemViewModel {
  return {
    id: 0,
    title: "",
    category: "",
    description: "",
    order: 0,
    type: "checklist",
  };
}
